import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { toast } from 'sonner';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { SelectField } from '@/components/select-field';
import { Banknote, Plus, Wallet } from 'lucide-react';
import { addLedgerEntry, fetchDriverPayouts, payDriverPayout } from '@/services/driver-payout';
import { fetchDriverLedger, fetchDrivers } from '@/services/driver';
import { cn, errorHandler } from '@/lib/utils';
// Locale-aware day formatting (e.g. "Jul 25, 2026") so the queue reads like a
// date instead of a raw ISO string. `undefined` locale defers to the browser /
// app language, matching the internal photos feature's date rendering.
function formatDay(iso) {
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}
const bucketSchema = z.enum(['upcoming', 'outstanding', 'overdue_only', 'on_hold', 'paid']);
const searchSchema = z.object({
    bucket: bucketSchema.default('upcoming'),
});
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/payouts/')({
    component: PayoutQueue,
    validateSearch: zodValidator(searchSchema),
});
// "Outstanding" = past the due date with money still owed. The backend cron
// owns the pending → due → overdue transition; the frontend must trust that
// state machine and not second-guess pending rows that happen to be past
// their due date (cron lag would otherwise misclassify untouched rows). The
// only row we re-bucket by date is partially_paid, because an installation-
// day advance flips that state regardless of whether the period is over —
// and a future-due partially_paid row belongs in Upcoming, not Outstanding.
function bucketForPayout(p, today) {
    if (p.state === 'cancelled')
        return null;
    if (p.state === 'on_hold')
        return 'on_hold';
    if (p.state === 'paid')
        return 'paid';
    if (p.state === 'overdue')
        return 'overdue_only';
    if (p.state === 'pending')
        return 'upcoming';
    if (p.state === 'due')
        return 'outstanding';
    if (p.state === 'partially_paid') {
        const due = new Date(p.dueDate);
        due.setHours(0, 0, 0, 0);
        return due.getTime() < today.getTime() ? 'outstanding' : 'upcoming';
    }
    return null;
}
// overdue_only is a strict subset of outstanding — both should appear under
// the broader "Outstanding" tab, while the dedicated "Overdue only" tab
// surfaces just the overdue rows.
function bucketMatchesFilter(bucket, filter) {
    if (filter === 'outstanding')
        return bucket === 'outstanding' || bucket === 'overdue_only';
    return bucket === filter;
}
const STATE_VARIANT = {
    pending: 'outline',
    due: 'secondary',
    overdue: 'destructive',
    partially_paid: 'secondary',
    paid: 'default',
    on_hold: 'outline',
    cancelled: 'outline',
};
const STATE_FILTERS = [
    {
        bucket: 'upcoming',
        labelKey: 'payouts.filter.upcoming',
        activeClass: 'bg-blue-500 text-white hover:bg-blue-600 border-transparent',
        inactiveClass: 'border-blue-500/40 text-blue-500 hover:bg-blue-500/10',
    },
    {
        bucket: 'outstanding',
        labelKey: 'payouts.filter.outstanding',
        activeClass: 'bg-amber-500 text-white hover:bg-amber-600 border-transparent',
        inactiveClass: 'border-amber-500/40 text-amber-500 hover:bg-amber-500/10',
    },
    {
        bucket: 'overdue_only',
        labelKey: 'payouts.filter.overdue_only',
        activeClass: 'bg-red-500 text-white hover:bg-red-600 border-transparent',
        inactiveClass: 'border-red-500/40 text-red-500 hover:bg-red-500/10',
    },
    {
        bucket: 'on_hold',
        labelKey: 'payouts.filter.on_hold',
        activeClass: 'bg-slate-500 text-white hover:bg-slate-600 border-transparent',
        inactiveClass: 'border-slate-500/40 text-slate-500 hover:bg-slate-500/10',
    },
    {
        bucket: 'paid',
        labelKey: 'payouts.filter.paid',
        activeClass: 'bg-emerald-500 text-white hover:bg-emerald-600 border-transparent',
        inactiveClass: 'border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10',
    },
];
function PayoutQueue() {
    const { t } = useTranslation('internal');
    const navigate = Route.useNavigate();
    const { bucket } = Route.useSearch();
    const [addEntryOpen, setAddEntryOpen] = React.useState(false);
    const { data: allPayouts = [], isLoading } = useQuery({
        queryKey: ['driver-payouts'],
        queryFn: () => fetchDriverPayouts(),
    });
    // Bucket each payout once so the filter buttons and the visible-rows
    // filter agree (and so today only crosses the date threshold once per
    // render).
    const buckets = React.useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const map = new Map();
        for (const p of allPayouts) {
            const b = bucketForPayout(p, today);
            if (b !== null)
                map.set(p.publicId, b);
        }
        return map;
    }, [allPayouts]);
    const counts = React.useMemo(() => {
        const c = {
            upcoming: 0,
            outstanding: 0,
            overdue_only: 0,
            on_hold: 0,
            paid: 0,
        };
        for (const p of allPayouts) {
            const b = buckets.get(p.publicId);
            if (!b)
                continue;
            // overdue_only also counts toward outstanding so the broader tab
            // shows the full set of past-due rows.
            if (b === 'overdue_only') {
                c.overdue_only++;
                c.outstanding++;
            }
            else {
                c[b]++;
            }
        }
        return c;
    }, [allPayouts, buckets]);
    const visiblePayouts = React.useMemo(() => allPayouts.filter((p) => {
        const b = buckets.get(p.publicId);
        return b !== undefined && bucketMatchesFilter(b, bucket);
    }), [allPayouts, buckets, bucket]);
    const missingPhotos = visiblePayouts.filter((p) => !p.hasPhoto).length;
    // Grouped by currency since a workspace can hold mixed-currency drivers
    // (e.g. KHR motodops + USD tuk-tuks). Paid rows still get summed when
    // the user filters to "Paid" so the badge reflects what was disbursed.
    const totalsByCurrency = React.useMemo(() => {
        const totals = {};
        for (const p of visiblePayouts) {
            const remaining = parseFloat(p.remaining);
            if (Number.isFinite(remaining) && remaining > 0) {
                totals[p.currency] = (totals[p.currency] ?? 0) + remaining;
            }
        }
        return Object.entries(totals)
            .filter(([, v]) => v > 0)
            .map(([currency, amount]) => ({ currency, amount: amount.toFixed(2) }));
    }, [visiblePayouts]);
    return (<div className="flex flex-col gap-6 p-6">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <h1 className="text-3xl font-semibold">{t('payouts.title')}</h1>
                    <p className="text-sm text-muted-foreground">
                        {t('payouts.summary', {
            due: counts.outstanding,
            overdue: counts.overdue_only,
            missing: missingPhotos,
        })}
                    </p>
                    {totalsByCurrency.length > 0 && (<p className="text-sm">
                            <span className="text-muted-foreground">{td(t, 'payouts.total_due_in_view')} </span>
                            <span className="font-semibold tabular-nums">
                                {totalsByCurrency.map((row) => `${row.currency} ${row.amount}`).join(' · ')}
                            </span>
                        </p>)}
                </div>
                <Button onClick={() => setAddEntryOpen(true)}>
                    <Plus className="h-4 w-4"/>
                    {td(t, 'payouts.action.add_entry')}
                </Button>
            </div>

            <div className="flex flex-wrap gap-2">
                {STATE_FILTERS.map((f) => {
            const isActive = f.bucket === bucket;
            const count = counts[f.bucket];
            return (<Button key={f.labelKey} variant="outline" size="sm" onClick={() => navigate({ search: { bucket: f.bucket } })} className={cn('gap-2', isActive ? f.activeClass : f.inactiveClass)}>
                            {td(t, f.labelKey)}
                            <Badge variant="outline" className={cn('h-5 min-w-5 px-1 text-[10px] tabular-nums', isActive
                    ? 'bg-white/20 text-white border-transparent'
                    : 'border-current bg-transparent')}>
                                {count}
                            </Badge>
                        </Button>);
        })}
            </div>

            {isLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : visiblePayouts.length === 0 ? (<Card>
                    <CardContent className="py-12 text-center text-sm text-muted-foreground">
                        {t('payouts.empty')}
                    </CardContent>
                </Card>) : (<Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">{t('payouts.table.status')}</th>
                                    <th className="px-3 py-2 text-left">{t('payouts.table.driver')}</th>
                                    <th className="px-3 py-2 text-left">{t('payouts.table.campaign')}</th>
                                    <th className="px-3 py-2 text-left">{t('payouts.table.period')}</th>
                                    <th className="px-3 py-2 text-right">{t('payouts.table.amount')}</th>
                                    <th className="px-3 py-2 text-left">{t('payouts.table.photos')}</th>
                                    <th className="px-3 py-2 text-left">{t('payouts.table.due')}</th>
                                    <th className="px-3 py-2 text-left">{t('payouts.table.method')}</th>
                                    <th className="px-3 py-2"/>
                                </tr>
                            </thead>
                            <tbody>
                                {visiblePayouts.map((payout) => (<PayoutRow key={payout.publicId} payout={payout}/>))}
                            </tbody>
                        </table>
                    </div>
                </Card>)}

            <AddPayoutEntryDialog open={addEntryOpen} onOpenChange={setAddEntryOpen}/>
        </div>);
}
function PayoutRow({ payout }) {
    const { t } = useTranslation('internal');
    const [payOpen, setPayOpen] = React.useState(false);
    const [addEntryOpen, setAddEntryOpen] = React.useState(false);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(payout.dueDate);
    const isDueToday = due.toDateString() === today.toDateString();
    // Mirrors the detail page's `canPayInFull`: open period, not held, money
    // still owed. Lets employees settle straight from the queue without
    // opening each payout one by one.
    const isOpen = payout.state !== 'paid' && payout.state !== 'cancelled';
    const canPay = isOpen && payout.state !== 'on_hold' && payout.remaining !== '0.00';
    return (<tr className={cn('border-t transition-colors hover:bg-muted/30', payout.state === 'overdue' && 'bg-red-500/5', payout.state === 'due' && isDueToday && 'bg-amber-500/5')}>
            <td className="px-3 py-2">
                <Badge variant={STATE_VARIANT[payout.state]}>{td(t, `enum.payout_state.${payout.state}`)}</Badge>
            </td>
            <td className="px-3 py-2">
                <p className="font-medium">{payout.driverName}</p>
            </td>
            <td className="px-3 py-2 text-muted-foreground">{payout.campaignName ?? '—'}</td>
            <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                {formatDay(payout.periodStart)} → {formatDay(payout.periodEnd)}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
                <p>
                    {payout.currency} {payout.totalOwed}
                </p>
                {payout.paidTotal !== '0.00' && payout.remaining !== '0.00' && (<p className="text-xs text-muted-foreground">
                        {payout.currency} {payout.remaining} left
                    </p>)}
            </td>
            <td className="px-3 py-2">
                {payout.hasPhoto ? (<span className="text-emerald-500 text-xs">{t('payouts.photo_received')}</span>) : payout.assignmentPublicId ? (<Link to="/console/internal/photos/new" search={{ assignment: payout.assignmentPublicId }} className="text-amber-500 text-xs hover:underline">
                        {t('payouts.photo_missing_log')}
                    </Link>) : (<span className="text-amber-500 text-xs">{t('payouts.photo_missing')}</span>)}
            </td>
            <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDay(payout.dueDate)}</td>
            <td className="px-3 py-2 text-xs">
                {td(t, `enum.payout_method.${payout.driverPayoutMethod}`, {
            defaultValue: payout.driverPayoutMethod,
        })}
            </td>
            <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end gap-1.5">
                    {isOpen && (<Button size="sm" variant="ghost" title={td(t, 'payouts.action.add_entry')} onClick={() => setAddEntryOpen(true)}>
                            <Wallet className="h-4 w-4"/>
                        </Button>)}
                    {canPay && (<Button size="sm" onClick={() => setPayOpen(true)}>
                            <Banknote className="h-4 w-4"/>
                            {t('payouts.action.pay')}
                        </Button>)}
                    <Button asChild size="sm" variant={payout.state === 'overdue' && !canPay ? 'default' : 'outline'}>
                        <Link to="/console/internal/payouts/$publicId" params={{ publicId: payout.publicId }}>
                            {t('common.open')}
                        </Link>
                    </Button>
                </div>
            </td>
            <PayPayoutDialog open={payOpen} onOpenChange={setPayOpen} payout={payout}/>
            <PayoutRowEntryDialog open={addEntryOpen} onOpenChange={setAddEntryOpen} payout={payout}/>
        </tr>);
}
// Per-row "add entry" — scoped to this payout's driver + period so it can post
// straight to the payout without the driver/period pickers the top-level dialog
// needs. Opening it inline avoids bouncing the user to the detail page.
function PayoutRowEntryDialog({ open, onOpenChange, payout, }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const defaults = { kind: 'advance', amount: '', method: 'cash', reference: '', notes: '' };
    const form = useForm({ defaultValues: defaults });
    React.useEffect(() => {
        if (open) {
            form.reset(defaults);
        }
        // defaults is a fresh literal each render; reset only when `open` flips.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, form]);
    const kind = form.watch('kind');
    const isCash = ENTRY_KIND_OPTIONS.find((o) => o.value === kind)?.cash ?? false;
    const kindOptions = ENTRY_KIND_OPTIONS.map((o) => ({
        value: o.value,
        label: td(t, `payouts.kind_option.${o.value}`),
    }));
    const methodOptions = ['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'].map((value) => ({
        value,
        label: td(t, `enum.payout_method.${value}`),
    }));
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => addLedgerEntry({
            publicId: payout.publicId,
            data: {
                kind: data.kind,
                amount: data.amount,
                method: isCash ? data.method : undefined,
                reference: isCash ? data.reference : undefined,
                notes: data.notes,
            },
        }),
        onSuccess: () => {
            toast.success(td(t, 'payouts.toast.entry_recorded'));
            queryClient.invalidateQueries({ queryKey: ['driver-payouts'] });
            queryClient.invalidateQueries({ queryKey: ['driver-payout', payout.publicId] });
            queryClient.invalidateQueries({ queryKey: ['driver-ledger', payout.driverPublicId] });
            onOpenChange(false);
        },
        onError: errorHandler,
    });
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{td(t, 'payouts.add_entry_dialog.title')}</DialogTitle>
                    <DialogDescription>
                        {payout.driverName} · {formatDay(payout.periodStart)} → {formatDay(payout.periodEnd)}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                        <SelectField control={form.control} name="kind" label={td(t, 'payouts.add_entry_dialog.kind')} options={kindOptions} disabled={isPending}/>
                        <TextField control={form.control} name="amount" label={td(t, 'payouts.add_entry_dialog.amount', { currency: payout.currency })} placeholder={td(t, 'payouts.add_entry_dialog.amount_placeholder')} disabled={isPending}/>
                        {isCash && (<SelectField control={form.control} name="method" label={td(t, 'payouts.add_entry_dialog.payment_method')} options={methodOptions} disabled={isPending}/>)}
                        {isCash && (<TextField control={form.control} name="reference" label={td(t, 'payouts.add_entry_dialog.reference')} disabled={isPending}/>)}
                        <TextAreaField control={form.control} name="notes" label={td(t, 'payouts.add_entry_dialog.notes')} placeholder={td(t, 'payouts.add_entry_dialog.notes_placeholder')} disabled={isPending}/>
                    </form>
                </Form>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={form.handleSubmit((data) => mutate(data))} disabled={isPending || !form.watch('amount')}>
                        {td(t, 'payouts.add_entry_dialog.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
function PayPayoutDialog({ open, onOpenChange, payout, }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const methodValues = ['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'];
    // Pre-select the driver's configured method when it's a known option so the
    // common case is a single confirm; fall back to cash otherwise.
    const defaultMethod = methodValues.includes(payout.driverPayoutMethod)
        ? payout.driverPayoutMethod
        : 'cash';
    const form = useForm({
        defaultValues: { method: defaultMethod, reference: '', notes: '' },
    });
    React.useEffect(() => {
        if (open) {
            form.reset({ method: defaultMethod, reference: '', notes: '' });
        }
    }, [open, defaultMethod, form]);
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => payDriverPayout({ publicId: payout.publicId, data }),
        onSuccess: () => {
            toast.success(t('payouts.toast.paid'));
            queryClient.invalidateQueries({ queryKey: ['driver-payouts'] });
            queryClient.invalidateQueries({ queryKey: ['driver-payout', payout.publicId] });
            onOpenChange(false);
        },
        onError: errorHandler,
    });
    const methodOptions = methodValues.map((value) => ({
        value,
        label: td(t, `enum.payout_method.${value}`),
    }));
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('payouts.pay_dialog.title', { name: payout.driverName })}</DialogTitle>
                    <DialogDescription>
                        {t('payouts.pay_dialog.desc', {
            currency: payout.currency,
            amount: payout.remaining,
            periodStart: payout.periodStart.slice(0, 10),
            periodEnd: payout.periodEnd.slice(0, 10),
        })}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                        <SelectField control={form.control} name="method" label={t('payouts.pay_dialog.method')} options={methodOptions} disabled={isPending}/>
                        <TextField control={form.control} name="reference" label={t('payouts.pay_dialog.reference')} disabled={isPending}/>
                        <TextAreaField control={form.control} name="notes" label={t('payouts.pay_dialog.notes')} disabled={isPending}/>
                    </form>
                </Form>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={form.handleSubmit((data) => mutate(data))} disabled={isPending}>
                        <Banknote className="h-4 w-4"/>
                        {t('payouts.pay_dialog.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
const ENTRY_KIND_OPTIONS = [
    { value: 'advance', cash: true },
    { value: 'settlement', cash: true },
    { value: 'bonus', cash: true },
    { value: 'extra', cash: false },
    { value: 'deduction', cash: false },
];
function AddPayoutEntryDialog({ open, onOpenChange, }) {
    const { t } = useTranslation('internal');
    const queryClient = useQueryClient();
    const form = useForm({
        defaultValues: {
            driverPublicId: '',
            payoutPublicId: '',
            kind: 'advance',
            amount: '',
            method: 'cash',
            reference: '',
            notes: '',
        },
    });
    React.useEffect(() => {
        if (open) {
            form.reset({
                driverPublicId: '',
                payoutPublicId: '',
                kind: 'advance',
                amount: '',
                method: 'cash',
                reference: '',
                notes: '',
            });
        }
    }, [open, form]);
    const driverPublicId = form.watch('driverPublicId');
    const kind = form.watch('kind');
    const isCash = ENTRY_KIND_OPTIONS.find((o) => o.value === kind)?.cash ?? false;
    const { data: drivers = [], isLoading: driversLoading } = useQuery({
        queryKey: ['internal-drivers'],
        queryFn: fetchDrivers,
        enabled: open,
    });
    const { data: ledger, isFetching: ledgerLoading } = useQuery({
        queryKey: ['driver-ledger', driverPublicId],
        queryFn: () => fetchDriverLedger(driverPublicId),
        enabled: open && !!driverPublicId,
    });
    const openPeriods = (ledger?.periods ?? []).filter((p) => p.state !== 'paid' && p.state !== 'cancelled' && p.state !== 'on_hold');
    const driverOptions = drivers.map((d) => ({
        value: d.publicId,
        label: `${d.fullName} · ${d.phoneNumber}`,
    }));
    const periodOptions = openPeriods.map((p) => ({
        value: p.payoutPublicId,
        label: `${p.periodStart.slice(0, 10)} → ${p.periodEnd.slice(0, 10)}${p.campaignName ? ` · ${p.campaignName}` : ''} · remaining ${p.currency} ${p.remaining}`,
    }));
    const kindOptions = ENTRY_KIND_OPTIONS.map((o) => ({
        value: o.value,
        label: td(t, `payouts.kind_option.${o.value}`),
    }));
    const methodOptions = ['cash', 'aba_pay', 'wing', 'acleda_bank', 'other'].map((value) => ({
        value,
        label: td(t, `enum.payout_method.${value}`),
    }));
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => addLedgerEntry({
            publicId: data.payoutPublicId,
            data: {
                kind: data.kind,
                amount: data.amount,
                method: isCash ? data.method : undefined,
                reference: isCash ? data.reference : undefined,
                notes: data.notes,
            },
        }),
        onSuccess: () => {
            toast.success(td(t, 'payouts.toast.entry_recorded'));
            queryClient.invalidateQueries({ queryKey: ['driver-payouts'] });
            queryClient.invalidateQueries({ queryKey: ['driver-payout'] });
            queryClient.invalidateQueries({ queryKey: ['driver-ledger', driverPublicId] });
            onOpenChange(false);
        },
        onError: errorHandler,
    });
    const showNoPeriods = !!driverPublicId && !ledgerLoading && periodOptions.length === 0;
    return (<Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{td(t, 'payouts.add_entry_dialog.title')}</DialogTitle>
                    <DialogDescription>{td(t, 'payouts.add_entry_dialog.desc')}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                        <SelectField control={form.control} name="driverPublicId" label={td(t, 'payouts.add_entry_dialog.driver')} options={driverOptions} disabled={isPending || driversLoading} onChange={() => form.setValue('payoutPublicId', '')}/>

                        {driverPublicId && ledgerLoading && (<p className="text-sm text-muted-foreground">
                                {td(t, 'payouts.add_entry_dialog.loading_periods')}
                            </p>)}

                        {showNoPeriods && (<p className="text-sm text-muted-foreground">
                                {td(t, 'payouts.add_entry_dialog.no_open_periods_prefix')}{' '}
                                <Link to="/console/internal/drivers/$publicId" params={{ publicId: driverPublicId }} className="text-primary hover:underline" onClick={() => onOpenChange(false)}>
                                    {td(t, 'payouts.add_entry_dialog.open_driver_link')}
                                </Link>{' '}
                                {td(t, 'payouts.add_entry_dialog.no_open_periods_suffix')}
                            </p>)}

                        {periodOptions.length > 0 && (<>
                                <SelectField control={form.control} name="payoutPublicId" label={td(t, 'payouts.add_entry_dialog.period')} options={periodOptions} disabled={isPending}/>
                                <SelectField control={form.control} name="kind" label={td(t, 'payouts.add_entry_dialog.kind')} options={kindOptions} disabled={isPending}/>
                                <TextField control={form.control} name="amount" label={td(t, 'payouts.add_entry_dialog.amount', {
                currency: ledger?.currency ?? '',
            })} placeholder={td(t, 'payouts.add_entry_dialog.amount_placeholder')} disabled={isPending}/>
                                {isCash && (<SelectField control={form.control} name="method" label={td(t, 'payouts.add_entry_dialog.payment_method')} options={methodOptions} disabled={isPending}/>)}
                                {isCash && (<TextField control={form.control} name="reference" label={td(t, 'payouts.add_entry_dialog.reference')} disabled={isPending}/>)}
                                <TextAreaField control={form.control} name="notes" label={td(t, 'payouts.add_entry_dialog.notes')} placeholder={td(t, 'payouts.add_entry_dialog.notes_placeholder')} disabled={isPending}/>
                            </>)}
                    </form>
                </Form>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    {periodOptions.length > 0 && (<Button onClick={form.handleSubmit((data) => mutate(data))} disabled={isPending || !form.watch('payoutPublicId') || !form.watch('amount')}>
                            {td(t, 'payouts.add_entry_dialog.submit')}
                        </Button>)}
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
